import React from 'react';
import Helmet from 'react-helmet';
import { StaticQuery, graphql } from 'gatsby';
import PropTypes from 'prop-types';
import SchemaOrg from './SchemaOrg';
import { useIntl } from 'react-intl';
import { isAmpPage, retrieveDescription, retrieveTitle } from '../../components/utils';

const SEO = ({ postData, frontmatter = {}, postImage, isBlogPost, horoscopeData=undefined }) => (
  <StaticQuery
    query={graphql`
      {
        site {
          siteMetadata {
            title
            description
            siteUrl
            image
            author {
              name
            }
            organization {
              name
              url
              logo
            }
            social {
              twitter
              fbAppID
            }
          }
        }
      }
    `}
    render={({ site: { siteMetadata: seo } }) => {
      const postMeta = frontmatter || postData.childMarkdownRemark.frontmatter || {};

      const intl = useIntl();
      const title = horoscopeData ? retrieveTitle(horoscopeData.pageDate, intl, horoscopeData.sign) : postMeta.title || seo.title;
      const ogType = horoscopeData ? 'article' : 'website';
      const description = horoscopeData
        ? retrieveDescription(horoscopeData.pageDate, intl, horoscopeData.sign)
        : postMeta.description || seo.description;

      const image = postImage ? `${seo.siteUrl}${postImage}` : seo.image;

      const url = postMeta.slug ? `${seo.siteUrl}/${postMeta.slug}` : seo.siteUrl;
      const datePublished = isBlogPost ? postMeta.datePublished : false;

      return (
        <React.Fragment>
          <Helmet>
            {/* General tags */}
            <meta name="keywords" content="daily horoscope,fortune telling,حظك اليوم,توقعات الابراج" />
            <title>{title}</title>
            <meta name="description" content={description} />
            <meta name="image" content={image} />
            {isAmpPage(url) && <link rel="canonical" href={url} />}

            {/* OpenGraph tags */}
            <meta property="og:url" content={url} />
            {isBlogPost ? <meta property="og:type" content="article" /> : null}
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:image" content={image} />
            <meta property="og:image:url" content={image} />
            <meta property="og:image:secure_url" content={image} />
            <meta property="og:type" content={ogType} />

            <meta property="fb:app_id" content={seo.social.fbAppID} />

            {/* Twitter Card tags */}
            <meta name="twitter:card" content="summary" />
            <meta name="twitter:site" content={seo.social.twitter} />
            <meta name="twitter:creator" content={seo.social.twitter} />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={image} />

            {!isAmpPage(url) && <link rel="amphtml" href={`${url}amp`} />}
          </Helmet>
          <SchemaOrg
            isBlogPost={isBlogPost}
            url={url}
            title={title}
            image={image}
            description={description}
            datePublished={datePublished}
            siteUrl={seo.siteUrl}
            author={seo.author}
            organization={seo.organization}
            defaultTitle={seo.title}
          />
        </React.Fragment>
      );
    }}
  />
);

SEO.propTypes = {
  isBlogPost: PropTypes.bool,
  postData: PropTypes.shape({
    childMarkdownRemark: PropTypes.shape({
      frontmatter: PropTypes.any,
      excerpt: PropTypes.any,
    }),
  }),
  postImage: PropTypes.string,
};

SEO.defaultProps = {
  isBlogPost: false,
  postData: { childMarkdownRemark: {} },
  postImage: null,
};

export default SEO;
